import type { IModelObject } from "@/app/services/modelObject";
import { makeRef } from "@/components/composables/reactivityTypes";
import type { Maybe } from "@/components/utils/typeHelpers";

//
// ModelObjects USED to contain their own dirtyState and it was automatically initialised
// on API response deserialisation. However, that approach was resource intensive (in particular
// causing slowness on mobile devices handling large payloads) so we have moved to finer control.
// The old approach can be used on a type by code-genning it with "WithTsDirtyCheck()".
// The new approach here pulls Dirty Checking out to this class, which can be invoked and
// used exactly where needed to check for dirtiness of a specific object, thus only using resources
// for the specific dirtiness being checked, not all uses of that object type.
//
export class DirtyChecker<T extends IModelObject> {
  private _item = makeRef<T | null>(null);
  private _checkpoint = makeRef("");

  constructor(item?: T) {
    this._item.value = item || null;
    this.resetCheckpoint();
  }

  public setItem(item: T) {
    this._item.value = item;
    this.resetCheckpoint();
  }

  public resetCheckpoint() {
    this._checkpoint.value = this.evaluateCheckpoint(this._item.value);
  }

  public isDirty() {
    return this._checkpoint.value !== this.evaluateCheckpoint(this._item.value);
  }

  private evaluateCheckpoint(item: T | null) {
    return item ? JSON.stringify(item.toProto()) : "";
  }
}

export function dirtyCheck<T extends IModelObject>(
  checkPoint: Maybe<T>,
  testVal: Maybe<T>
) {
  return (
    (checkPoint ? JSON.stringify(checkPoint.toProto()) : "") !==
    (testVal ? JSON.stringify(testVal.toProto()) : "")
  );
}
